import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Chip,
  Breadcrumbs,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  ArrowForward as ArrowIcon,
} from "@mui/icons-material";
import { get, patch } from "../utils/networkUtils";

const TournamentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [matchdays, setMatchdays] = useState([]);
  const [gameweeks, setGameweeks] = useState([]);

  // Editable fields
  const [formData, setFormData] = useState({
    status: "draft",
    max_transfers: 40,
    max_substitutions: 5,
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Check if this is World Cup
  const isWorldCup =
    tournament?.competition_name?.toLowerCase().includes("world cup") ||
    tournament?.competition_name?.toLowerCase().includes("fifa world");

  // Fetch tournament details
  const fetchTournament = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await get(`/admin/tournament-calendar/${id}`);

      if (response?.data) {
        const t = response.data;
        setTournament(t);
        setFormData({
          status: t.status || "draft",
          max_transfers: t.max_transfers || 40,
          max_substitutions: t.max_substitutions || 5,
        });
      } else {
        setError("Tournament not found");
      }
    } catch (err) {
      console.error("Failed to fetch tournament:", err);
      setError("Failed to load tournament details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch matchdays for this tournament
  const fetchMatchdays = useCallback(async () => {
    if (!isWorldCup) return;

    try {
      const response = await get(
        `/admin/matchday?tournamentCalendarId=${id}&limit=100`
      );
      if (response?.data?.matchdays) {
        setMatchdays(response.data.matchdays);
      }
    } catch (err) {
      console.error("Failed to fetch matchdays:", err);
    }
  }, [id, isWorldCup]);

  // Fetch gameweeks for this tournament
  const fetchGameweeks = useCallback(async () => {
    if (isWorldCup) return;

    try {
      const response = await get(
        `/admin/gameweek?tournamentCalendarId=${id}&limit=100`
      );
      if (response?.data?.gameweeks) {
        setGameweeks(response.data.gameweeks);
      }
    } catch (err) {
      console.error("Failed to fetch gameweeks:", err);
    }
  }, [id, isWorldCup]);

  useEffect(() => {
    fetchTournament();
  }, [fetchTournament]);

  useEffect(() => {
    fetchMatchdays();
  }, [fetchMatchdays]);

  useEffect(() => {
    fetchGameweeks();
  }, [fetchGameweeks]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      await patch(`/admin/tournament-calendar/${id}`, {
        status: formData.status,
        max_transfers: parseInt(formData.max_transfers, 10),
        max_substitutions: parseInt(formData.max_substitutions, 10),
      });

      setSuccessMessage("Tournament updated successfully");
      setHasChanges(false);

      // Refresh tournament data
      await fetchTournament();
    } catch (err) {
      console.error("Failed to update tournament:", err);
      setError(err?.response?.data?.message || "Failed to update tournament");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !tournament) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/dashboard/tournaments")}
          sx={{ mt: 2 }}
        >
          Back to Tournaments
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {tournament?.competition_name || "Tournament"} | DreamTeam Admin
        </title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Typography
            component="span"
            onClick={() => navigate("/dashboard/tournaments")}
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Tournaments
          </Typography>
          <Typography color="text.primary">
            {tournament?.competition_name}
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4">{tournament?.competition_name}</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {tournament?.season_label}
              {isWorldCup && (
                <Chip
                  label="World Cup - Matchdays"
                  color="primary"
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
              {!isWorldCup && (
                <Chip
                  label="Gameweeks"
                  color="secondary"
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate("/dashboard/tournaments")}
            >
              Back
            </Button>
            {hasChanges && (
              <Button
                variant="contained"
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                onClick={handleSave}
                disabled={saving}
              >
                Save Changes
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Column - Tournament Info */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Tournament Information
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  OPTA Data (Read-only)
                </Typography>

                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Competition Name"
                      secondary={tournament?.competition_name || "N/A"}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Season"
                      secondary={tournament?.season_label || "N/A"}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Start Date"
                      secondary={formatDate(tournament?.start_date)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="End Date"
                      secondary={formatDate(tournament?.end_date)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Short Code"
                      secondary={tournament?.short_code || "N/A"}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="OPTA ID"
                      secondary={
                        tournament?.opta_tournament_calendar_id || "N/A"
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Editable Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Fantasy Settings
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  These settings can be edited
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      label="Status"
                      value={formData.status}
                      onChange={(e) =>
                        handleFieldChange("status", e.target.value)
                      }
                      helperText='Setting a tournament to "Active" makes it the tournament the public app uses for team creation and the current matchday. Only one should be active at a time.'
                    >
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="paused">Paused</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="archived">Archived</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Transfers"
                      value={formData.max_transfers}
                      onChange={(e) =>
                        handleFieldChange("max_transfers", e.target.value)
                      }
                      helperText="Maximum transfers allowed per team"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Substitutions"
                      value={formData.max_substitutions}
                      onChange={(e) =>
                        handleFieldChange("max_substitutions", e.target.value)
                      }
                      helperText="Maximum substitutions allowed per team across the tournament"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Matchdays Section (World Cup only) */}
          {isWorldCup && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">
                      Matchdays ({matchdays.length})
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          navigate(`/dashboard/tournaments/${id}/matchdays`)
                        }
                      >
                        Manage Matchdays
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() =>
                          navigate(
                            `/dashboard/tournaments/${id}/matchday-builder`
                          )
                        }
                      >
                        Open Matchday Builder
                      </Button>
                    </Box>
                  </Box>

                  {matchdays.length === 0 ? (
                    <Alert severity="info">
                      No matchdays created yet. Click "Manage Matchdays" to
                      create matchdays.
                    </Alert>
                  ) : (
                    <List>
                      {matchdays.map((md) => (
                        <ListItemButton
                          key={md.id}
                          onClick={() =>
                            navigate(
                              `/dashboard/tournaments/${id}/matchdays/${md.id}`
                            )
                          }
                        >
                          <ListItemText
                            primary={`Matchday ${md.matchdayNumber}: ${md.name}`}
                            secondary={`Status: ${md.status} | Expected: ${
                              md.expectedMatchCount
                            } matches | Assigned: ${md.assignedMatches ?? 0}`}
                          />
                          <ArrowIcon fontSize="small" />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Gameweeks Section (Other tournaments) */}
          {!isWorldCup && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">
                      Gameweeks ({gameweeks.length})
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          navigate(`/dashboard/tournaments/${id}/gameweeks`)
                        }
                      >
                        Manage Gameweeks
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() =>
                          navigate(
                            `/dashboard/tournaments/${id}/gameweek-builder`
                          )
                        }
                      >
                        Open Gameweek Builder
                      </Button>
                    </Box>
                  </Box>

                  {gameweeks.length === 0 ? (
                    <Alert severity="info">
                      No gameweeks created yet. Click "Manage Gameweeks" to
                      create gameweeks.
                    </Alert>
                  ) : (
                    <List>
                      {gameweeks.map((gw) => (
                        <ListItemButton
                          key={gw.id}
                          onClick={() =>
                            navigate(
                              `/dashboard/tournaments/${id}/gameweeks/${gw.id}`
                            )
                          }
                        >
                          <ListItemText
                            primary={`Gameweek ${gw.number}: ${gw.name}`}
                            secondary={`Status: ${gw.status} | Assigned: ${
                              gw.assignedMatches ?? 0
                            } matches`}
                          />
                          <ArrowIcon fontSize="small" />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  );
};

export default TournamentDetailPage;
